import * as THREE from 'three';
import { box, weld } from '../level/VoxelFactory.js';
import { eventBus, Events } from '../core/EventBus.js';
import { gameState } from '../core/GameState.js';
import {
  FLOOD, COLORS, ROW_DEPTH, FIELD_HALF, WRAP_HALF, TILE_X, KIDS,
} from '../core/Constants.js';
import { DeathCause } from '../systems/CollisionSystem.js';

export const FloodState = {
  IDLE: 'idle',
  WARNING: 'warning',
  SURGE: 'surge',
};

function buildWaterWaveGeometry(spanRows = 3) {
  const D = COLORS.WATER_DEEP || 0x1d4ed8;
  const M = COLORS.WATER_MID || 0x3b82f6;
  const F = COLORS.WATER_FOAM || 0xe0f2fe;

  const width = (WRAP_HALF + 4) * 2; // ~56 units wide, filling the entire horizontal track span!
  const depth = (spanRows + 0.4) * ROW_DEPTH;
  const height = 2.4;

  const parts = [
    // 1. Base deep water channel volume
    box(width, height * 0.4, depth, D, 0, height * 0.2, 0),
    // 2. Mid water churning crest
    box(width - 0.6, height * 0.45, depth - 0.6, M, 0.2, height * 0.55, 0),
    // 3. Wave front lip
    box(width - 0.2, height * 0.35, depth * 0.45, M, width * 0.2, height * 0.8, -depth * 0.2),
    // 4. Foam top spray & crest ridges
    box(width - 1.2, 0.3, depth - 0.8, F, 0.3, height * 0.95, 0),
    box(width * 0.5, 0.4, depth * 0.5, F, width * 0.25, height * 1.05, -depth * 0.15),
  ];

  // Staggered spray droplets along wave front
  for (let i = -5; i <= 5; i++) {
    const x = i * 2.2;
    parts.push(box(0.8, 0.3, 0.4, F, x, height * 1.1, -depth * 0.25));
  }

  return weld(parts);
}

export class FloodSystem {
  constructor(scene, materials) {
    this.scene = scene;
    this.material = materials.solid;

    this.spanRows = FLOOD.ROW_SPAN || 3;
    this.waveGeometry = buildWaterWaveGeometry(this.spanRows);
    this.waveMesh = new THREE.Mesh(this.waveGeometry, this.material);
    this.group = new THREE.Group();
    this.group.add(this.waveMesh);
    scene.add(this.group);

    this.state = FloodState.IDLE;
    this.stateTimer = 0;
    this.surgeProgress = 0;
    this.dir = 1;
    this.waveX = 0;
    this.startRow = 1;
    this.endRow = 3;
    this.centerZ = 0;

    this.lanes = [];
    this.derailedConsists = [];

    this.resetTimer();
    this.hide();
  }

  setLanes(lanes) {
    this.lanes = lanes;
    this.reset();
  }

  reset() {
    this.state = FloodState.IDLE;
    this.stateTimer = 0;
    this.surgeProgress = 0;
    this.resetTimer();
    this.hide();

    for (const c of this.derailedConsists) {
      c.restoreService();
    }
    this.derailedConsists.length = 0;
  }

  resetTimer() {
    const min = FLOOD.INTERVAL_MIN || 18.0;
    const max = FLOOD.INTERVAL_MAX || 32.0;
    this.floodTimer = min + Math.random() * (max - min);
  }

  hide() {
    this.group.visible = false;
  }

  show() {
    this.group.visible = true;
  }

  triggerFlood() {
    const rowCount = gameState.level.rows ? gameState.level.rows.length : 10;
    if (rowCount < 3) return;

    const span = FLOOD.ROW_SPAN || 3;
    this.spanRows = span;
    const maxStart = Math.max(1, rowCount - span - 1);
    this.startRow = Math.floor(1 + Math.random() * maxStart);
    this.endRow = Math.min(rowCount - 1, this.startRow + span - 1);

    this.centerZ = -((this.startRow + this.endRow) / 2) * ROW_DEPTH;
    this.dir = Math.random() < 0.5 ? 1 : -1;
    this.waveX = -this.dir * (WRAP_HALF + 16);

    this.group.position.set(this.waveX, -2.5, this.centerZ);
    this.group.rotation.y = this.dir > 0 ? 0 : Math.PI;

    this.state = FloodState.WARNING;
    this.stateTimer = FLOOD.WARN_DURATION || 1.4;

    eventBus.emit('flood:warn', {
      startRow: this.startRow,
      endRow: this.endRow,
      dir: this.dir,
    });
  }

  update(dt, elapsed) {
    if (!gameState.game.isPlaying && !gameState.game.started) {
      this.hide();
      return;
    }

    // ── Update Derailed Consists ─────────────────────────────────────
    // 1. Tumbles with 3D physics for first 1.5s
    // 2. Shrinks & vanishes at exactly 2.0 seconds
    // 3. Train service continues on lane at exactly 5.0 seconds
    for (let i = this.derailedConsists.length - 1; i >= 0; i--) {
      const c = this.derailedConsists[i];
      c.derailTimer = (c.derailTimer || 0) + dt;

      if (c.derailTimer < 1.5) {
        c.updatePhysics(dt);
      } else if (c.derailTimer < 2.0) {
        c.updatePhysics(dt);
        const shrink = Math.max(0, (2.0 - c.derailTimer) / 0.5);
        c.group.scale.set(shrink, shrink, shrink);
      } else if (c.derailTimer < 5.0) {
        c.group.visible = false;
      } else {
        // Exactly 5 seconds after derailment, train service resumes!
        c.restoreService();
        this.derailedConsists.splice(i, 1);
      }
    }

    switch (this.state) {
      case FloodState.IDLE: {
        this.hide();
        this.floodTimer -= dt;
        if (this.floodTimer <= 0) {
          this.triggerFlood();
        }
        break;
      }

      case FloodState.WARNING: {
        this.stateTimer -= dt;
        if (this.stateTimer <= 0) {
          this.state = FloodState.SURGE;
          this.surgeProgress = 0;
          this.show();
          eventBus.emit('flood:surge', { dir: this.dir });
        }
        break;
      }

      case FloodState.SURGE: {
        this.surgeProgress += dt;

        // ── 1. Rush in from side (0.0s to 0.7s) ──────────────────────
        // ── 2. Fill track completely for EXACTLY 2.0 seconds (0.7s to 2.7s) ──
        // ── 3. Subside through floor (2.7s to 3.9s) ──────────────────

        const rushDuration = 0.7;
        const fillDuration = 2.0; // Fills track for 2 seconds!
        const subsideDuration = 1.2;
        const totalDuration = rushDuration + fillDuration + subsideDuration;

        let y = 1.2;
        let currentX = 0;

        if (this.surgeProgress < rushDuration) {
          // Rush in horizontally from left or right side
          const t = this.surgeProgress / rushDuration;
          const startX = -this.dir * (WRAP_HALF + 16);
          currentX = startX + t * (0 - startX);
          y = -2.0 + t * 3.2; // rises as it rushes in
        } else if (this.surgeProgress < rushDuration + fillDuration) {
          // Fills the track horizontally for exactly 2 seconds!
          currentX = 0;
          const t = this.surgeProgress - rushDuration;
          y = 1.2 + Math.sin(t * 12.0) * 0.15; // churning water filling track
        } else if (this.surgeProgress < totalDuration) {
          // Subsides down through the floor!
          currentX = 0;
          const t = (this.surgeProgress - (rushDuration + fillDuration)) / subsideDuration;
          y = 1.2 - t * 4.5; // goes through floor down to -3.3
        } else {
          this.state = FloodState.IDLE;
          this.resetTimer();
          this.hide();
          break;
        }

        this.waveX = currentX;
        this.group.position.set(this.waveX, y, this.centerZ);

        // Churning wave foam bobbing animation
        this.splashTimer = (this.splashTimer || 0) + dt;
        if (this.splashTimer >= 0.16) {
          this.splashTimer = 0;
          eventBus.emit('water:splash', { x: this.waveX, z: this.centerZ });
        }

        if (y > 0.0) {
          this.checkTrainDisplacement();
          this.checkPlayerCollision(y);
        }
        break;
      }
    }

    this.checkDisplacedTrainPlayerCollision();
  }

  setPlayer(player) {
    this.player = player;
  }

  checkTrainDisplacement() {
    const waveMinX = this.waveX - (WRAP_HALF + 4);
    const waveMaxX = this.waveX + (WRAP_HALF + 4);

    for (const lane of this.lanes) {
      if (lane.row < this.startRow || lane.row > this.endRow) continue;

      for (const consist of lane.items) {
        if (consist.derailed) continue;

        if (consist.max > waveMinX && consist.min < waveMaxX) {
          const impulseVx = this.dir * (18.0 + Math.random() * 8.0);
          const impulseVy = 2.0 + Math.random() * 3.0;
          const impulseVz = (Math.random() - 0.5) * 4.0;

          consist.derail(this.scene, lane, impulseVx, impulseVy, impulseVz);
          this.derailedConsists.push(consist);

          eventBus.emit('train:crash', { x: consist.x, z: consist.worldZ });
        }
      }
    }
  }

  checkPlayerCollision(y) {
    const p = gameState.player;
    if (!p || p.dead || gameState.time < p.invulnUntil || gameState.time < p.spicyImmunityUntil) return;

    const checkKids = (KIDS.CHECK_COLLISION ?? 1) === 1;
    const positions = (checkKids && this.player && this.player.getChildrenPositions)
      ? this.player.getChildrenPositions()
      : [{ x: p.x, row: p.row }];

    for (let i = 0; i < positions.length; i++) {
      const pos = positions[i];
      if (pos.row >= this.startRow && pos.row <= this.endRow && y > 0.0) {
        if (i === 0) {
          eventBus.emit(Events.PLAYER_DIE, { cause: DeathCause.FLOOD, x: p.x, row: p.row });
          return;
        } else {
          if (this.player && this.player.removeChild) {
            this.player.removeChild(i - 1, DeathCause.FLOOD, pos.x, pos.row);
          }
        }
      }
    }
  }

  checkDisplacedTrainPlayerCollision() {
    const p = gameState.player;
    if (!p || p.dead || gameState.time < p.invulnUntil) return;

    const pZ = -p.row * ROW_DEPTH;
    for (const c of this.derailedConsists) {
      if (c.derailTimer >= 2.0) continue;
      if (Math.abs(p.x - c.x) < c.halfLength + 0.8 && Math.abs(pZ - c.worldZ) < 1.8) {
        if (this.player && this.player.pushBack) {
          this.player.pushBack(this.dir, 1);
        }
        break;
      }
    }
  }

  dispose() {
    this.hide();
    this.group.removeFromParent();
    this.waveGeometry.dispose();
    for (const c of this.derailedConsists) {
      c.restoreService();
    }
    this.derailedConsists.length = 0;
  }
}
